import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Poster = () => {
    const [selectedFiles, setSelectedFiles] = useState({});
    const [message, setMessage] = useState('');
    const [posterUrls, setPosterUrls] = useState({});
    const [activePosters, setActivePosters] = useState([]);
    const [newPosterId, setNewPosterId] = useState('');
    const apiLocalUrl = process.env.REACT_APP_API_LOCAL_URL;

    const handleFileChange = (event, posterId) => {
        setSelectedFiles(prev => ({
            ...prev,
            [posterId]: event.target.files[0]
        }));
    };

    const handleUpload = async (posterId) => {
        const file = selectedFiles[posterId];
        if (!file) {
            setMessage(`Please select a file to upload for poster ${posterId}.`);
            return;
        }

        const formData = new FormData();
        formData.append('image', file);

        try {
            const response = await axios.post(
                `${apiLocalUrl}/upload-poster/${posterId}`, 
                formData, 
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            setMessage(response.data.message);
            fetchPoster(posterId);
            if (!activePosters.includes(posterId)) {
                setActivePosters(prev => [...prev, posterId]);
            }
        } catch (error) {
            setMessage(`Failed to upload poster ${posterId}. ${error.response?.data?.message || error.message}`);
        }
    };

    const fetchPoster = (posterId) => {
        const url = `${apiLocalUrl}/posters/poster${posterId}/poster.jpg?t=${Date.now()}`;
        setPosterUrls(prev => ({
            ...prev,
            [posterId]: url
        }));
    };

    const fetchAllPosters = async () => {
        try {
            const response = await axios.get(`${apiLocalUrl}/list-posters`);
            const posters = response.data.posters || [];
            setActivePosters(posters.map(p => p.id));
            
            const urls = {};
            posters.forEach(poster => {
                urls[poster.id] = `${apiLocalUrl}/posters/poster${poster.id}/poster.jpg?t=${Date.now()}`;
            });
            setPosterUrls(urls);
        } catch (error) {
            console.error('Failed to fetch posters:', error);
        }
    };

    const handleDelete = async (posterId) => {
        try {
            const response = await axios.delete(`${apiLocalUrl}/delete-poster/${posterId}`);
            setMessage(response.data || `Poster ${posterId} deleted successfully.`);
            setPosterUrls(prev => {
                const newUrls = {...prev};
                delete newUrls[posterId];
                return newUrls;
            });
            setActivePosters(prev => prev.filter(id => id !== posterId));
        } catch (error) {
            setMessage(`Failed to delete poster ${posterId}. ${error.response?.data || error.message}`);
        }
    };

    const handleAddNewPoster = () => {
        if (newPosterId && !activePosters.includes(newPosterId)) {
            setActivePosters(prev => [...prev, newPosterId]);
            setNewPosterId('');
        }
    };

    useEffect(() => {
        fetchAllPosters();
    }, []);

    return (
        <div className="max-w-6xl mx-auto my-8 p-6 bg-gradient-to-br from-gray-50 to-gray-100 shadow-xl rounded-2xl">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500">
                Poster Management Dashboard
            </h2>

            {/* Add New Poster Section */}
            <div className="mb-8 p-6 bg-white rounded-xl shadow-md border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add New Poster Slot
                </h3>
                <div className="flex gap-4 items-center">
                    <input
                        type="text"
                        value={newPosterId}
                        onChange={(e) => setNewPosterId(e.target.value)}
                        placeholder="Enter poster number (e.g., 1, 2, 3)"
                        className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50"
                    />
                    <button
                        onClick={handleAddNewPoster}
                        className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-3 rounded-lg font-medium transition-all shadow-lg hover:shadow-md transform hover:-translate-y-0.5"
                    >
                        Add New Slot
                    </button>
                </div>
            </div>

            {/* Posters Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {activePosters.map(posterId => (
                    <div key={posterId} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    Poster #{posterId}
                                </h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    posterUrls[posterId] 
                                        ? 'bg-green-100 text-green-800 border border-green-200' 
                                        : 'bg-amber-100 text-amber-800 border border-amber-200'
                                }`}>
                                    {posterUrls[posterId] ? 'Uploaded' : 'Empty'}
                                </span>
                            </div>

                            {/* File Input */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Image File
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, posterId)}
                                        className="block w-full text-sm text-gray-500
                                        file:mr-4 file:py-2 file:px-4
                                        file:rounded-lg file:border-0
                                        file:text-sm file:font-semibold
                                        file:bg-indigo-50 file:text-indigo-700
                                        hover:file:bg-indigo-100 file:transition-colors
                                        file:cursor-pointer"
                                    />
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 mb-4">
                                <button
                                    onClick={() => handleUpload(posterId)}
                                    disabled={!selectedFiles[posterId]}
                                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                                        selectedFiles[posterId] 
                                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg' 
                                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    }`}
                                >
                                    Upload Poster
                                </button>
                                <button
                                    onClick={() => handleDelete(posterId)}
                                    disabled={!posterUrls[posterId]}
                                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                                        posterUrls[posterId] 
                                            ? 'bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white shadow-md hover:shadow-lg' 
                                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    }`}
                                >
                                    Remove Poster
                                </button>
                            </div>

                            {/* Preview Section */}
                            <div className="mt-4">
                                <h4 className="text-lg font-medium text-gray-800 mb-2 flex items-center gap-2">
                                    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    Poster Preview
                                </h4>
                                <div className="bg-gray-50 rounded-lg p-4 min-h-[200px] flex items-center justify-center border-2 border-dashed border-gray-200">
                                    {posterUrls[posterId] ? (
                                        <div className="relative w-full h-full">
                                            <img
                                                src={posterUrls[posterId]}
                                                alt={`Poster ${posterId}`}
                                                className="max-h-64 w-full object-contain rounded-md shadow-sm"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '';
                                                    e.target.alt = 'Image failed to load';
                                                }}
                                            />
                                            {!posterUrls[posterId] && (
                                                <div className="absolute inset-0 flex items-center justify-center text-gray-400 italic">
                                                    Image failed to load
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-gray-400 italic flex flex-col items-center">
                                            <svg className="w-12 h-12 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            No image uploaded yet
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Status Message */}
            {message && (
                <div className={`mt-6 p-4 rounded-lg text-center font-medium ${
                    message.includes('success') 
                        ? 'bg-green-50 text-green-800 border border-green-200' 
                        : 'bg-rose-50 text-rose-800 border border-rose-200'
                }`}>
                    {message}
                </div>
            )}

            {/* Empty State */}
            {activePosters.length === 0 && (
                <div className="mt-8 p-8 bg-white rounded-xl shadow-sm border border-gray-200 text-center">
                    <div className="text-gray-400 mb-4">
                        <svg className="w-20 h-20 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-medium text-gray-700 mb-2">No Posters Available</h3>
                    <p className="text-gray-500 mb-4">Add your first poster slot using the form above</p>
                    <button
                        onClick={() => setNewPosterId('1')}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-all shadow-md inline-flex items-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create First Poster
                    </button>
                </div>
            )}
        </div>
    );
};

export default Poster;